import React, { useState } from 'react';
import { X, Save, User as UserIcon, Mail, Trash2, Lock, Building2, Check } from 'lucide-react';
import { User, UserRole } from '../lib/types';
import { sendWelcomeEmail } from '../lib/email';

interface Props {
  user: User | null;
  onClose: () => void;
  onSave: (user: User) => void;
  onDelete?: (userId: string) => void;
}

const UserModal: React.FC<Props> = ({ user, onClose, onSave, onDelete }) => {
  const isNewUser = user === null;
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<Partial<User>>(
    user || {
      name: '',
      email: '',
      role: 'trial',
      remainingCredits: 5,
      createdAt: new Date().toISOString(),
      password: ''
    }
  );
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);

  const handleSave = async () => {
    if (!formData.email || !formData.name || !formData.role || (isNewUser && !formData.password)) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsUpdating(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (isNewUser) {
        // Generate a unique ID for the new user
        const newUser: User = {
          id: `user-${Date.now()}`,
          email: formData.email,
          name: formData.name,
          role: formData.role as UserRole,
          remainingCredits: formData.role === 'trial' ? 5 : Infinity,
          createdAt: new Date().toISOString(),
          password: formData.password as string
        };

        // Generate login link (in a real app, this would be a proper URL)
        const loginLink = `${window.location.origin}/login?email=${encodeURIComponent(newUser.email)}`;

        // Send welcome email
        await sendWelcomeEmail(newUser, loginLink);
        setShowEmailSuccess(true);

        // Call onSave with the new user
        onSave(newUser);

        // Wait a moment to show the email success message before closing
        setTimeout(() => {
          onClose();
        }, 2000);
      } else if (user) {
        // Update existing user
        const updatedUser: User = {
          ...user,
          ...formData as User
        };
        onSave(updatedUser);
        onClose();
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Une erreur est survenue lors de la sauvegarde');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !onDelete) return;
    
    setIsDeleting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onDelete(user.id);
      onClose();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            {isNewUser ? 'Créer un utilisateur' : 'Détails de l\'utilisateur'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {showEmailSuccess && (
            <div className="mb-6 bg-green-50 text-green-700 p-4 rounded-md flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              <span>Email de bienvenue envoyé avec succès !</span>
            </div>
          )}

          <div className="space-y-4">
            {isNewUser && (
              <div className="flex items-center justify-center mb-6">
                <div className="bg-indigo-100 rounded-full p-3">
                  <UserIcon className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom complet *
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Jean Dupont"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="jean.dupont@example.com"
                required
              />
            </div>

            {isNewUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Le mot de passe doit contenir au moins 8 caractères
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rôle utilisateur *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="trial">Essai</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="entreprise">Entreprise</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>

            {!isNewUser && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date d'inscription</label>
                  <p className="mt-1 text-gray-900">{formatDate(formData.createdAt!)}</p>
                </div>

                {formData.lastLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Dernière connexion</label>
                    <p className="mt-1 text-gray-900">{formatDate(formData.lastLogin)}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Crédits restants</label>
                  <p className="mt-1 text-gray-900">
                    {formData.remainingCredits === Infinity ? 'Illimité' : formData.remainingCredits}
                  </p>
                </div>

                {formData.subscriptionEnd && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fin d'abonnement</label>
                    <p className="mt-1 text-gray-900">{formatDate(formData.subscriptionEnd)}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mt-6 flex justify-between">
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              {!isNewUser && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Supprimer</span>
                </button>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={isUpdating || !formData.email || !formData.name || (isNewUser && !formData.password)}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>
                {isUpdating ? 'Sauvegarde...' : isNewUser ? 'Créer l\'utilisateur' : 'Enregistrer'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmer la suppression
            </h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                {isDeleting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <span>Confirmer la suppression</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserModal;